<template>
  <ModalRoot :model-value="true">
    <template #body>
      <div
        class="fixed inset-0 z-50 !max-w-full !max-h-full !h-full bg-brand-bg-main"
      >
        <div
          class="h-full flex flex-col max-w-3xl mx-auto desktop:border-x desktop:border-brand-bgdivider-10 px-10 py-5 overflow-y-auto"
        >
          <SignupForm
            :validated-identifiers="validatedIdentifiers"
            :loading="loading"
            :errors="errors"
            @submit="submit"
            @revalidate="revalidate"
          />
        </div>
      </div>
    </template>
  </ModalRoot>
</template>

<script setup lang="ts">
import ModalRoot from "@/components/ui/modal/modal-root.vue";
import SignupForm from "./signup-form.vue";
import { useAwaitAction } from "@/composables/use-await-action";
import { graphql as gql } from "@/generated";
import type {
  AuthChannelType,
  DirectSignUpUserInput,
  UserStoreMeFragment,
  ValidatedUserIdentifier,
} from "@/generated/graphql";
import { useToastStore } from "@/stores/toast.store";
import { useMutation } from "@urql/vue";
import { ref } from "vue";
import { useReferralService } from "@/features/channel/services/use-referral-service";
import { useAuthSession } from "@/composables/auth/use-auth-session";
import type { ResultOf } from "@graphql-typed-document-node/core";
import type { UserStoreMe } from "@/stores/user.store";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  channelId: string;
  validatedIdentifiers: ValidatedUserIdentifier[];
  authChannelType: AuthChannelType;
  /**
   * Pre-signup JWT from the verifyAuthentication call that initiated this
   * signup flow. When present, the auth session composable will echo it to
   * the host SDK via `auth-modal:success` after signup completes — this is
   * how the in-iframe embed flow notifies the parent SDK.
   *
   * Pass `undefined` for SDK-pushed signup flows (the ones triggered by the
   * embed broker's `SIGN_UP_WITH_IDENTIFIERS` channel event) so the JWT the
   * SDK already has isn't echoed back to it.
   */
  sessionToken?: string;
}>();

const errors = ref<{
  email?: string;
  username?: string;
  phone_number?: string;
  date_of_birth?: string;
}>({});

const emit = defineEmits<{
  success: [user: UserStoreMe];
}>();

const DirectSignUpMutationDocument = gql(/* GraphQL */ `
  mutation SignUp($user: DirectSignUpUserInput!, $channelId: String!) {
    signUp(user: $user, channel_id: $channelId) {
      ... on DuplicateUserPhoneNumberError {
        error
        phone_number
      }
      ... on DuplicateUserEmailError {
        error
        email
      }
      ... on AgeRestrictionError {
        error
        message
      }
      ... on InvalidUserIdentifierConfirmationError {
        error
        identifier_type
      }
      ... on Me {
        ...UserStoreMe
      }
    }
  }
`);

const toastStore = useToastStore();
const referralService = useReferralService();
const { handleSignupComplete } = useAuthSession({ mode: "inApp" });
const { t } = useI18n();

function revalidate(field: string) {
  errors.value = {
    [field]: undefined,
  };
}

const { executeMutation: submitSignUp } = useMutation(
  DirectSignUpMutationDocument,
);
const { handler: submit, loading } = useAwaitAction(
  async (
    user: Omit<
      DirectSignUpUserInput,
      "validated_identifiers" | "community_ids"
    >,
  ) => {
    const communitiesToFollow = [];
    const referralCommunity =
      referralService.currentReferralData.value?.payload.community_id;

    if (referralCommunity) {
      communitiesToFollow.push(referralCommunity);
    }

    const response = await submitSignUp({
      channelId: props.channelId,
      user: {
        email: user.email,
        username: user.username,
        locale: user.locale,
        avatar: user.avatar,
        phone_number: user.phone_number,
        country_iso: user.country_iso,
        email_consent: user.email_consent,
        date_of_birth: user.date_of_birth,
        community_ids: communitiesToFollow,
        validated_identifiers: props.validatedIdentifiers,
      },
    });

    const result = response?.data?.signUp;

    if (response?.error || !result) {
      return toastStore.addToast({
        message: t(
          "channel.auth.identityStrategy.signup.failedSignUpInternalErrors",
          {
            errors: response?.error?.message || "Unknown error",
          },
        ),
        type: "error",
      });
    } else if ("error" in result) {
      switch (result.__typename) {
        case "DuplicateUserPhoneNumberError":
          errors.value = {
            phone_number: t(
              "channel.auth.identityStrategy.signup.duplicatePhoneNumber",
              {
                phoneNumber: result.phone_number,
              },
            ),
          };
          break;
        case "DuplicateUserEmailError":
          errors.value = {
            email: t("channel.auth.identityStrategy.signup.duplicateEmail"),
          };
          break;
        case "AgeRestrictionError":
          errors.value = {
            date_of_birth: t(
              "channel.auth.identityStrategy.signup.ageRestriction",
            ),
          };
          break;
        case "InvalidUserIdentifierConfirmationError":
          errors.value = {
            [result.identifier_type]: t(
              "channel.auth.identityStrategy.signup.couldNotVerifyIdentifier",
              {
                identifierType: result.identifier_type,
              },
            ),
          };
          break;
        default:
          toastStore.addToast({
            message: t(
              "channel.auth.identityStrategy.signup.failedSignUpInternalErrors",
              {
                errors: "",
              },
            ),
            type: "error",
          });
          break;
      }
      return;
    }

    const signedUpUser = result as ResultOf<UserStoreMeFragment>;
    await handleSignupComplete({
      user: signedUpUser,
      sessionToken: props.sessionToken,
      authChannelType: props.authChannelType,
    });

    emit("success", signedUpUser);
  },
);
</script>
